import { FieldHint } from "@/components/field-hint/FieldHint.tsx";
import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import {
  useEntityReferenceOptions,
  useEntityReferenceValues,
} from "@/hooks/use-entity-reference-options.hook.ts";
import { useSystemTableConfig } from "@/hooks/use-system-table-config.hook.ts";
import {
  ChangesPreview,
  ChangesPreviewItem,
} from "@/pages/employees/employee/components/ChangesPreview.tsx";
import { useEmployeeEmploymentSave } from "@/queries/employees/use-employee-employment.query.ts";
import { useEmploymentContractsSearchInline } from "@/queries/employment-contracts.query.ts";
import { useEmploymentTypesSearchInline } from "@/queries/employment-types.query.ts";
import { useLegalEntities } from "@/queries/use-legal-entities.query.ts";
import { useProbationPoliciesSearchInline } from "@/queries/use-probation-policies.query.ts";
import { useWorkPatternsSearchInline } from "@/queries/use-work-patterns.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { Rounding } from "@/types/common.types.ts";
import {
  ApiEmployeeEmployment,
  ApiEmployeeEmploymentPayload,
} from "@/types/employees/employee-employment.types.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { ApiProbationPolicy, ProbationPolicyUnitTypes } from "@/types/probation-policy.types.ts";
import { EmployeeFieldPermission } from "@/types/role.types.ts";
import { ApiFieldTypes, ApiTableTypes } from "@/types/custom-field.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  LinkButtonTo,
  Modal,
  ModalProps,
  Select,
  Stack,
  Textarea,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface EmployeeEmploymentEditModalProps extends ModalProps {
  employee: ApiEmployee;
  employeeEmployment?: ApiEmployeeEmployment;
}

function getProbationEndDate(dateStart: Date, policy: ApiProbationPolicy) {
  const dateEnd = new Date(dateStart);

  if (policy.unit === ProbationPolicyUnitTypes.months) {
    dateEnd.setMonth(dateEnd.getMonth() + policy.amount);
    dateEnd.setDate(dateEnd.getDate() - 1);
  } else if (policy.unit === ProbationPolicyUnitTypes.days) {
    dateEnd.setDate(dateEnd.getDate() + policy.amount);
  }

  return dateEnd;
}

function getActiveEntity<T extends { is_archived?: boolean }>(
  primary?: T | null,
  fallback?: T | null
) {
  const entity = primary ?? fallback;

  if (!entity || entity.is_archived) return undefined;

  return entity;
}

export function EmployeeEmploymentEditModal({
  employee,
  employeeEmployment,
  ...props
}: EmployeeEmploymentEditModalProps) {
  const { t } = useTranslation();
  const { renderEmployeeName, renderDate, getDaysToDate } = useFormatter();
  const { canEditField } = usePermissions();
  const { id } = { ...props };
  const { close } = useModals();
  const { fields, customFields, isMandatory } = useSystemTableConfig(
    ApiTableTypes.employee_employments
  );
  const {
    getFieldByType,
    getValueForFieldFromValueObject,
    getCustomFieldPreviewValue,
    getZodSchemaForField,
    transformFormDataToValue,
  } = useCustomFieldsHook();

  const optionalNumber = (type: ApiFieldTypes) =>
    isMandatory(type)
      ? z.number({ error: t("errors:field_required") })
      : z.number().nullable().optional();

  const EmploymentSchemaObject = z.object({
    date_effective_from: z.date({ error: t("errors:field_required") }),
    employment_contract_id: optionalNumber(ApiFieldTypes.employee_employment_contract),
    employment_type_id: optionalNumber(ApiFieldTypes.employee_employment_type),
    work_pattern_id: optionalNumber(ApiFieldTypes.employee_employment_work_pattern),
    legal_entity_id: optionalNumber(ApiFieldTypes.employee_employment_legal_entity),
    date_probation_ends: isMandatory(ApiFieldTypes.employee_employment_probation_date_end)
      ? z.date({ error: t("errors:field_required") })
      : z.date().nullable().optional(),
    comment: isMandatory(ApiFieldTypes.employee_employment_comment)
      ? z.string({ error: t("errors:field_required") }).min(1, t("errors:field_required"))
      : z.string().nullable().optional(),
    ...Object.assign({}, ...customFields.map((field) => getZodSchemaForField(field))),
  });

  const currentEmployment = useMemo(() => {
    const employeeCurrentEmployment = employee.employee_employment;

    return {
      id: employeeEmployment?.id ?? employeeCurrentEmployment?.id ?? 0,
      date_effective_from: employeeEmployment?.date_effective_from,
      comment: employeeEmployment?.comment,
      employment_contract: getActiveEntity(
        employeeEmployment?.employment_contract,
        employeeCurrentEmployment?.employment_contract
      ),
      employment_type: getActiveEntity(
        employeeEmployment?.employment_type,
        employeeCurrentEmployment?.employment_type
      ),
      work_pattern: getActiveEntity(
        employeeEmployment?.work_pattern,
        employeeCurrentEmployment?.work_pattern
      ),
      legal_entity: getActiveEntity(
        employeeEmployment?.legal_entity,
        employeeCurrentEmployment?.legal_entity
      ),
      date_probation_ends: employeeEmployment?.date_probation_ends,
      custom_fields: employeeEmployment?.custom_fields ?? [],
    };
  }, [employeeEmployment, employee]);

  const [changeItems, setChangeItems] = useState<ChangesPreviewItem[]>(() => {
    return [
      {
        id: "date_effective_from",
        title: t("common:date_effective_from"),
        currentValue: employeeEmployment?.id
          ? renderDate(employeeEmployment.date_effective_from)
          : "",
      },
      {
        id: "employment_contract_id",
        title: t("core:contract"),
        currentValue: currentEmployment.employment_contract?.title ?? "",
      },
      {
        id: "employment_type_id",
        title: t("core:employment_type"),
        currentValue: currentEmployment.employment_type?.title ?? "",
      },
      {
        id: "work_pattern_id",
        title: t("core:work_pattern"),
        currentValue: currentEmployment.work_pattern?.title ?? "",
      },
      {
        id: "legal_entity_id",
        title: t("core:legal_entity"),
        currentValue: currentEmployment.legal_entity?.title ?? "",
      },
      {
        id: "date_probation_ends",
        title: t("core:probation_till"),
        currentValue: currentEmployment.date_probation_ends
          ? renderDate(currentEmployment.date_probation_ends)
          : "",
      },
      {
        id: "comment",
        title: t("common:comment"),
        currentValue: currentEmployment.comment ?? "",
      },
      ...customFields.map((field) => {
        const customField = currentEmployment.custom_fields.find(
          (item) => item.field.id === field.id
        );
        const currentFieldData = getValueForFieldFromValueObject(field, customField?.value, true);
        return {
          id: field.slug,
          title: field.title,
          currentValue: getCustomFieldPreviewValue(
            field,
            currentFieldData,
            customField?.value?.value_references ?? []
          ),
        };
      }),
    ];
  });

  const [isPreviewChanges, setIsPreviewChanges] = useState(false);
  const [probationPolicyId, setProbationPolicyId] = useState<number | null>(null);
  const [probationPeriodEndForced, setProbationPeriodEndForced] = useState(
    !!currentEmployment.date_probation_ends
  );
  const [isCustomDateProbationEnd, setIsCustomDateProbationEnd] = useState(
    !!currentEmployment.date_probation_ends
  );
  const types = useEmploymentTypesSearchInline();
  const contracts = useEmploymentContractsSearchInline();
  const workPatterns = useWorkPatternsSearchInline();
  const { data: legalEntities, isLoading: isLegalEntitiesLoading } = useLegalEntities();
  const formHook = useForm({
    mode: "onSubmit",
    resolver: zodResolver(EmploymentSchemaObject),
    values: {
      date_effective_from: currentEmployment.date_effective_from as Date,
      employment_contract_id: currentEmployment.employment_contract?.id ?? null,
      employment_type_id: currentEmployment.employment_type?.id ?? null,
      work_pattern_id: currentEmployment.work_pattern?.id ?? null,
      legal_entity_id: currentEmployment.legal_entity?.id ?? null,
      date_probation_ends: currentEmployment.date_probation_ends,
      comment: currentEmployment.comment ?? "",
      ...Object.assign(
        {},
        ...customFields.map((field) => {
          const customField = currentEmployment.custom_fields.find(
            (item) => item.field.id === field.id
          );
          return getValueForFieldFromValueObject(field, customField?.value, true);
        })
      ),
    },
  });

  const {
    data: policies,
    options: policiesOptions,
    isLoading: policiesIsLoading,
  } = useProbationPoliciesSearchInline();

  const {
    control,
    register,
    getValues,
    watch,
    setValue,
    trigger,
    formState: { errors, isDirty },
  } = formHook;
  const referenceValues = useEntityReferenceValues(customFields, control);
  const { optionsByEntity: referenceOptionsByEntity } = useEntityReferenceOptions(
    customFields,
    referenceValues
  );

  const dateEffectiveFrom = watch("date_effective_from");
  const probationPeriodEnd = watch("date_probation_ends");
  const employeeEmploymentSave = useEmployeeEmploymentSave(control);
  const fieldConfig = useCallback(
    (type: ApiFieldTypes) => fields.find((field) => field.type === type),
    [fields]
  );
  const fieldOrder = useCallback(
    (type: ApiFieldTypes) => fields.findIndex((field) => field.type === type),
    [fields]
  );
  const fieldTitle = (type: ApiFieldTypes, fallback: string) =>
    fieldConfig(type)?.title?.trim() || fallback;

  const changePreview = useCallback((key: string, title: string) => {
    setChangeItems((prevItems) => {
      let hasChanges = false;

      const nextItems = prevItems.map((item) => {
        if (item.id !== key || item.newValue === title) return item;

        hasChanges = true;
        return { ...item, newValue: title };
      });

      return hasChanges ? nextItems : prevItems;
    });
  }, []);

  const canEditEmployment = useMemo(() => {
    return canEditField(employee, EmployeeFieldPermission.manage_employee_employment);
  }, [canEditField, employee]);

  const selectedProbationPolicy = useMemo(() => {
    return policies?.find((policy) => policy.id === probationPolicyId);
  }, [policies, probationPolicyId]);

  useEffect(() => {
    if (!dateEffectiveFrom || !selectedProbationPolicy || probationPeriodEndForced) return;

    const nextDate = getProbationEndDate(dateEffectiveFrom, selectedProbationPolicy);
    const currentTime = probationPeriodEnd ? new Date(probationPeriodEnd).getTime() : undefined;
    const nextTime = nextDate.getTime();

    if (currentTime === nextTime) return;

    setValue("date_probation_ends", nextDate, { shouldDirty: true });
    changePreview("date_probation_ends", renderDate(nextDate));
  }, [
    changePreview,
    dateEffectiveFrom,
    probationPeriodEnd,
    probationPeriodEndForced,
    renderDate,
    selectedProbationPolicy,
    setValue,
  ]);

  const dateEndInfo = useMemo(() => {
    if (!dateEffectiveFrom || !probationPolicyId || !selectedProbationPolicy) return;

    const dateEnd = !probationPeriodEndForced
      ? getProbationEndDate(dateEffectiveFrom, selectedProbationPolicy)
      : probationPeriodEnd
        ? new Date(probationPeriodEnd)
        : undefined;

    if (!dateEnd) return;

    const dateEmployment = new Date(dateEnd);
    dateEmployment.setDate(dateEmployment.getDate() + 1);

    return (
      <Stack className={"py-2 text-sm text-secondary"} direction={"horizontal"}>
        {t("core:ends_on_period", {
          period_end: renderDate(dateEnd),
          days_to_end: t("plurals:plural_day", {
            count: getDaysToDate(dateEmployment, Rounding.up),
          }),
        })}
        {canEditEmployment && (
          <>
            &nbsp;
            <LinkButtonTo
              onClick={() => {
                setIsCustomDateProbationEnd(true);
              }}
              className={"text-sm text-secondary"}
            >
              {t("common:change")}
            </LinkButtonTo>
          </>
        )}
      </Stack>
    );
  }, [
    canEditEmployment,
    dateEffectiveFrom,
    getDaysToDate,
    probationPeriodEnd,
    probationPeriodEndForced,
    probationPolicyId,
    renderDate,
    selectedProbationPolicy,
    t,
  ]);

  const onBack = useCallback(() => {
    if (!isPreviewChanges) return undefined;
    return () => {
      setIsPreviewChanges(false);
    };
  }, [isPreviewChanges]);

  const getPayload = useCallback((): ApiEmployeeEmploymentPayload => {
    return {
      date_effective_from: getValues("date_effective_from"),
      ...(fieldConfig(ApiFieldTypes.employee_employment_type) && {
        employment_type_id: getValues("employment_type_id") ?? null,
      }),
      ...(fieldConfig(ApiFieldTypes.employee_employment_contract) && {
        employment_contract_id: getValues("employment_contract_id") ?? null,
      }),
      ...(fieldConfig(ApiFieldTypes.employee_employment_work_pattern) && {
        work_pattern_id: getValues("work_pattern_id") ?? null,
      }),
      ...(fieldConfig(ApiFieldTypes.employee_employment_legal_entity) && {
        legal_entity_id: getValues("legal_entity_id") ?? null,
      }),
      ...(fieldConfig(ApiFieldTypes.employee_employment_probation_date_end) && {
        date_probation_ends: getValues("date_probation_ends") ?? null,
      }),
      ...(fieldConfig(ApiFieldTypes.employee_employment_comment) && {
        comment: getValues("comment")?.trim() || null,
      }),
      custom_fields: Object.fromEntries(
        customFields.map((field) => [field.slug, transformFormDataToValue(field, getValues())])
      ),
    };
  }, [customFields, fieldConfig, getValues, transformFormDataToValue]);

  const handleActionButton = useCallback(async () => {
    if (isPreviewChanges) {
      try {
        await employeeEmploymentSave.mutateAsync({
          employeeId: employee.id,
          employmentId: employeeEmployment?.id,
          payload: getPayload(),
        });

        if (employeeEmployment) {
          analyticsService.trackEvent(analyticEvents.employeeEmployments.edited, {
            [analyticProperties.id]: employee.id,
          });
        } else {
          analyticsService.trackEvent(analyticEvents.employeeEmployments.created, {
            [analyticProperties.id]: employee.id,
          });
        }

        close(id);
      } catch {
        return;
      }
    } else {
      const isValid = await trigger();
      if (!isValid) return;

      const employmentValues = getValues();
      setChangeItems((prevItems) =>
        prevItems.map((item) => {
          const field = customFields.find((customField) => customField.slug === item.id);
          if (!field) return item;
          return {
            ...item,
            newValue: getCustomFieldPreviewValue(
              field,
              employmentValues,
              field.reference_entity ? (referenceOptionsByEntity[field.reference_entity] ?? []) : []
            ),
          };
        })
      );
      setIsPreviewChanges(true);
    }
  }, [
    close,
    customFields,
    employee.id,
    employeeEmployment,
    employeeEmploymentSave,
    getCustomFieldPreviewValue,
    getPayload,
    getValues,
    id,
    isPreviewChanges,
    referenceOptionsByEntity,
    trigger,
  ]);

  const actionsButton = useMemo(() => {
    let buttonText: string = t("common:next");

    if (isPreviewChanges) {
      buttonText = isDirty ? t("common:save") : t("common:close");
    }

    return (
      <Stack>
        <Button
          size={"lg"}
          onClick={handleActionButton}
          isLoading={employeeEmploymentSave.isPending}
        >
          {buttonText}
        </Button>
      </Stack>
    );
  }, [employeeEmploymentSave.isPending, handleActionButton, isDirty, isPreviewChanges, t]);

  return (
    <Modal
      onBack={onBack()}
      title={isPreviewChanges ? t("core:preview_changes") : t("core:employment")}
      caption={renderEmployeeName(employee)}
      variant={"side"}
      layout={"stretch"}
      actions={actionsButton}
      withCloser={true}
      {...props}
    >
      <Stack>
        {isPreviewChanges && <ChangesPreview items={changeItems} />}
        {!isPreviewChanges && (
          <>
            <Title
              header={t("core:contract_details")}
              paddingTop={false}
              paddingBottom
              size={"xs"}
            />
            <form>
              <Stack>
                {fieldConfig(ApiFieldTypes.effective_from) && (
                  <Stack style={{ order: fieldOrder(ApiFieldTypes.effective_from) }} gap="xs">
                    <Controller
                      render={({ field: { value, onChange, name } }) => (
                        <Datepicker
                          value={value}
                          disabled={!canEditEmployment}
                          onChange={(value) => {
                            onChange(value);
                            changePreview(name, value ? renderDate(value) : "");
                          }}
                          required={true}
                          label={fieldTitle(
                            ApiFieldTypes.effective_from,
                            t("common:date_effective_from")
                          )}
                          error={errors.date_effective_from?.message as string | undefined}
                        />
                      )}
                      control={control}
                      name={"date_effective_from"}
                    />
                    <FieldHint hint={fieldConfig(ApiFieldTypes.effective_from)?.hint} />
                  </Stack>
                )}

                {fieldConfig(ApiFieldTypes.employee_employment_contract) && (
                  <Stack
                    style={{ order: fieldOrder(ApiFieldTypes.employee_employment_contract) }}
                    gap="xs"
                  >
                    <Controller
                      render={({ field: { value, onChange, name } }) => (
                        <Select
                          label={fieldTitle(
                            ApiFieldTypes.employee_employment_contract,
                            t("core:contract")
                          )}
                          emptyTitle={t("common:select")}
                          isLoading={contracts.isLoading}
                          value={value}
                          searchable
                          isClearable
                          required={isMandatory(ApiFieldTypes.employee_employment_contract)}
                          disabled={!canEditEmployment}
                          onChange={(value, entity) => {
                            onChange(value);
                            changePreview(name, entity?.title ?? "");
                          }}
                          onSearchOptions={(text) => contracts.search(text)}
                          searchOptions={contracts.options}
                          error={errors.employment_contract_id?.message as string | undefined}
                        />
                      )}
                      control={control}
                      name={"employment_contract_id"}
                    />
                    <FieldHint
                      hint={fieldConfig(ApiFieldTypes.employee_employment_contract)?.hint}
                    />
                  </Stack>
                )}
                {fieldConfig(ApiFieldTypes.employee_employment_type) && (
                  <Stack
                    style={{ order: fieldOrder(ApiFieldTypes.employee_employment_type) }}
                    gap="xs"
                  >
                    <Controller
                      render={({ field: { value, onChange, name } }) => (
                        <Select
                          label={fieldTitle(
                            ApiFieldTypes.employee_employment_type,
                            t("core:employment_type")
                          )}
                          emptyTitle={t("common:select")}
                          isLoading={types.isLoading}
                          value={value}
                          searchable
                          disabled={!canEditEmployment}
                          isClearable
                          required={isMandatory(ApiFieldTypes.employee_employment_type)}
                          onChange={(value, entity) => {
                            onChange(value);
                            changePreview(name, entity?.title ?? "");
                          }}
                          onSearchOptions={(text) => types.search(text)}
                          searchOptions={types.options}
                          error={errors.employment_type_id?.message as string | undefined}
                        />
                      )}
                      control={control}
                      name={"employment_type_id"}
                    />
                    <FieldHint hint={fieldConfig(ApiFieldTypes.employee_employment_type)?.hint} />
                  </Stack>
                )}

                {fieldConfig(ApiFieldTypes.employee_employment_work_pattern) && (
                  <Stack
                    style={{ order: fieldOrder(ApiFieldTypes.employee_employment_work_pattern) }}
                    gap="xs"
                  >
                    <Controller
                      render={({ field: { value, onChange, name } }) => (
                        <Select
                          label={fieldTitle(
                            ApiFieldTypes.employee_employment_work_pattern,
                            t("core:work_pattern")
                          )}
                          emptyTitle={t("common:select")}
                          isLoading={workPatterns.isLoading}
                          value={value}
                          searchable
                          disabled={!canEditEmployment}
                          isClearable
                          required={isMandatory(ApiFieldTypes.employee_employment_work_pattern)}
                          onChange={(value, entity) => {
                            onChange(value);
                            changePreview(name, entity?.title ?? "");
                          }}
                          onSearchOptions={(text) => workPatterns.search(text)}
                          searchOptions={workPatterns.options}
                          error={errors.work_pattern_id?.message as string | undefined}
                        />
                      )}
                      control={control}
                      name={"work_pattern_id"}
                    />
                    <FieldHint
                      hint={fieldConfig(ApiFieldTypes.employee_employment_work_pattern)?.hint}
                    />
                  </Stack>
                )}

                {fieldConfig(ApiFieldTypes.employee_employment_legal_entity) && (
                  <Stack
                    style={{ order: fieldOrder(ApiFieldTypes.employee_employment_legal_entity) }}
                    gap="xs"
                  >
                    <Controller
                      render={({ field: { value, onChange, name } }) => (
                        <Select
                          label={fieldTitle(
                            ApiFieldTypes.employee_employment_legal_entity,
                            t("core:legal_entity")
                          )}
                          emptyTitle={t("common:select")}
                          isLoading={isLegalEntitiesLoading}
                          options={legalEntities}
                          value={value}
                          searchable
                          disabled={!canEditEmployment}
                          isClearable
                          required={isMandatory(ApiFieldTypes.employee_employment_legal_entity)}
                          onChange={(value, entity) => {
                            onChange(value);
                            changePreview(name, entity?.title ?? "");
                          }}
                          error={errors.legal_entity_id?.message as string | undefined}
                        />
                      )}
                      control={control}
                      name={"legal_entity_id"}
                    />
                    <FieldHint
                      hint={fieldConfig(ApiFieldTypes.employee_employment_legal_entity)?.hint}
                    />
                  </Stack>
                )}

                {fieldConfig(ApiFieldTypes.employee_employment_probation_date_end) && (
                  <Stack
                    style={{
                      order: fieldOrder(ApiFieldTypes.employee_employment_probation_date_end),
                    }}
                    gap="xs"
                  >
                    <Select
                      value={probationPolicyId}
                      options={policiesOptions}
                      label={t("core:select_probation_policy")}
                      emptyTitle={t("common:select")}
                      isLoading={policiesIsLoading}
                      isClearable
                      disabled={!canEditEmployment}
                      onChange={(value) => {
                        const nextPolicyId = value == null ? null : Number(value);

                        setProbationPolicyId(nextPolicyId);

                        if (nextPolicyId === null) {
                          setProbationPeriodEndForced(!!probationPeriodEnd);
                          setIsCustomDateProbationEnd(true);
                          return;
                        }

                        setProbationPeriodEndForced(false);
                        setIsCustomDateProbationEnd(false);
                      }}
                    />
                    {dateEndInfo}
                    {(isCustomDateProbationEnd || !probationPolicyId) && (
                      <Controller
                        render={({ field: { value, onChange, name } }) => (
                          <Datepicker
                            value={value}
                            disabled={!canEditEmployment}
                            isClearable
                            onChange={(value) => {
                              onChange(value);
                              setProbationPeriodEndForced(true);
                              changePreview(name, value ? renderDate(value) : "");
                            }}
                            label={fieldTitle(
                              ApiFieldTypes.employee_employment_probation_date_end,
                              t("core:probation_till")
                            )}
                            required={isMandatory(
                              ApiFieldTypes.employee_employment_probation_date_end
                            )}
                            error={errors.date_probation_ends?.message as string | undefined}
                          />
                        )}
                        control={control}
                        name={"date_probation_ends"}
                      />
                    )}
                    <FieldHint
                      hint={fieldConfig(ApiFieldTypes.employee_employment_probation_date_end)?.hint}
                    />
                  </Stack>
                )}

                {fieldConfig(ApiFieldTypes.employee_employment_comment) && (
                  <Stack
                    style={{ order: fieldOrder(ApiFieldTypes.employee_employment_comment) }}
                    gap="xs"
                  >
                    <Textarea
                      disabled={!canEditEmployment}
                      onInput={(el) => {
                        changePreview("comment", el.currentTarget?.value ?? "");
                      }}
                      label={fieldTitle(
                        ApiFieldTypes.employee_employment_comment,
                        t("core:add_comment_to_employment")
                      )}
                      required={isMandatory(ApiFieldTypes.employee_employment_comment)}
                      {...register("comment")}
                    />
                    <FieldHint
                      hint={fieldConfig(ApiFieldTypes.employee_employment_comment)?.hint}
                    />
                  </Stack>
                )}
                {customFields.map((field) => (
                  <Stack key={field.id} style={{ order: fields.indexOf(field) }} gap="xs">
                    {getFieldByType({
                      field,
                      formHook,
                      canView: true,
                      canEdit: canEditEmployment,
                      label: field.title,
                      options: field.options.map((option) => ({
                        id: Number(option.id),
                        title: option.title,
                      })),
                    })}
                  </Stack>
                ))}
              </Stack>
            </form>
          </>
        )}
      </Stack>
    </Modal>
  );
}
